import { Alert, Button, Flex, Form, Input, Modal, Space, Typography } from 'antd';
import {
  useCreateMyArtifactMutation,
  useRetryMyArtifactTranscriptionMutation,
  useUpdateMyArtifactMutation,
  type UpdateMyArtifactPayload
} from 'api/ArtifactsApi';
import { ArtifactAudioField, ArtifactAudioFieldHandle } from 'components/Artifacts/ArtifactAudioField';
import { ArtifactImageField } from 'components/Artifacts/ArtifactImageField';
import { ArtifactMemoryDatePicker } from 'components/Artifacts/ArtifactMemoryDatePicker';
import { ArtifactTranscriptPanel } from 'components/Artifacts/ArtifactTranscriptPanel';
import { ArtifactEditorMode, ArtifactMediaFieldState } from 'components/Artifacts/editorTypes';
import { Artifact } from 'components/Artifacts/types';
import SpeechAntdTextArea, { SpeechAntdTextAreaHandle } from 'components/UiKit/SpeechAntdTextArea/SpeechAntdTextArea';
import { useEffect, useMemo, useRef, useState } from 'react';

const { Text } = Typography;

type ArtifactEditorModalProps = {
  open: boolean;
  mode: ArtifactEditorMode;
  artifact?: Artifact | null;
  onClose: () => void;
  onSaved?: (artifact: Artifact) => void;
};

const emptyMediaState: ArtifactMediaFieldState = {
  media: null,
  previewUrl: null,
  phase: 'idle',
  progressPercent: 0,
  errorMessage: '',
  fileName: ''
};

const getApiErrorMessage = (error: any) => {
  if (error?.status === 409) {
    return 'This artifact was updated elsewhere. Refresh to review the latest version before saving.';
  }

  return error?.data?.message || 'We could not save this artifact right now. Please try again.';
};

const sectionBadgeStyle = {
  alignItems: 'center',
  background: 'linear-gradient(180deg, #7c6cff 0%, #5f4bf4 100%)',
  borderRadius: '999px',
  color: '#fff',
  display: 'inline-flex',
  fontSize: 12,
  fontWeight: 700,
  height: 20,
  justifyContent: 'center',
  minWidth: 20
} as const;

const sectionLabelStyle = {
  color: '#26233a',
  fontSize: 15,
  fontWeight: 600,
  lineHeight: '22px'
} as const;

const inputStyle = {
  boxSizing: 'border-box',
  borderColor: '#e7e8f3',
  borderRadius: 12,
  boxShadow: 'none',
  maxWidth: '100%',
  minHeight: 44,
  minWidth: 0,
  width: '100%'
} as const;

const sectionContainerStyle = {
  background: '#fff',
  border: '1px solid #f1f2f8',
  borderRadius: 16,
  padding: 16
} as const;

const fieldCounterWrapStyle = {
  display: 'flex',
  justifyContent: 'flex-end',
  width: '100%'
} as const;

const fieldCounterTextStyle = {
  color: '#a0a6bb',
  fontSize: 12
} as const;

const requiredStarStyle = {
  color: '#ff4d4f',
  fontSize: 16,
  fontWeight: 700,
  lineHeight: 1
} as const;

const renderSectionTitle = (step: number, label: string, helper?: string, required = false) => (
  <Flex align="center" justify="space-between" gap={12} style={{ width: '100%' }}>
    <Flex align="center" gap={8} wrap="wrap">
      <span style={sectionBadgeStyle}>{step}</span>
      <span style={sectionLabelStyle}>{label}</span>
      {helper ? <Text style={{ color: '#9aa0b4', fontSize: 13, fontWeight: 500 }}>{helper}</Text> : null}
    </Flex>
    {required ? (
      <span aria-label={`${label} is required`} title={`${label} is required`} style={requiredStarStyle}>
        *
      </span>
    ) : null}
  </Flex>
);

export function ArtifactEditorModal({ open, mode, artifact = null, onClose, onSaved }: ArtifactEditorModalProps) {
  const [form] = Form.useForm();
  const audioFieldRef = useRef<ArtifactAudioFieldHandle>(null);
  const descriptionRef = useRef<SpeechAntdTextAreaHandle>(null);
  const imageStateRef = useRef<ArtifactMediaFieldState>(emptyMediaState);
  const audioStateRef = useRef<ArtifactMediaFieldState>(emptyMediaState);
  const [saveErrorMessage, setSaveErrorMessage] = useState('');
  const [imageState, setImageState] = useState<ArtifactMediaFieldState>(emptyMediaState);
  const [audioState, setAudioState] = useState<ArtifactMediaFieldState>(emptyMediaState);
  const [isPreparingSave, setIsPreparingSave] = useState(false);
  const [transcriptDraft, setTranscriptDraft] = useState('');
  const [currentRevision, setCurrentRevision] = useState(artifact?.revision || 0);
  const [currentTranscript, setCurrentTranscript] = useState(artifact?.transcript || null);
  const [createArtifact, { isLoading: isCreating }] = useCreateMyArtifactMutation();
  const [updateArtifact, { isLoading: isUpdating }] = useUpdateMyArtifactMutation();
  const [retryTranscription, { isLoading: isRetryingTranscription }] = useRetryMyArtifactTranscriptionMutation();

const syncImageState = useMemo(
  () => (nextState: ArtifactMediaFieldState) => {
    imageStateRef.current = nextState;
    setImageState(nextState);
  },
  []
);

const syncAudioState = useMemo(
  () => (nextState: ArtifactMediaFieldState) => {
    audioStateRef.current = nextState;
    setAudioState(nextState);
  },
  []
);

  useEffect(() => {
    if (!open) {
      return;
    }

    form.setFieldsValue({
      title: artifact?.title || '',
      description: artifact?.description || '',
      memoryDate: artifact?.memoryDate || null,
      memoryDateLabel: artifact?.memoryDateLabel || ''
    });
    setTranscriptDraft(artifact?.transcript?.text || '');
    setCurrentRevision(artifact?.revision || 0);
    setCurrentTranscript(artifact?.transcript || null);
    setSaveErrorMessage('');
    syncImageState(
      artifact?.image
        ? {
            ...emptyMediaState,
            media: artifact.image,
            previewUrl: artifact.image.url || artifact.image.thumbnailUrl || null,
            phase: 'ready',
            progressPercent: 100
          }
        : emptyMediaState
    );
    syncAudioState(
      artifact?.audio
        ? {
            ...emptyMediaState,
            media: artifact.audio,
            previewUrl: artifact.audio.url || null,
            phase: 'ready',
            progressPercent: 100
          }
        : emptyMediaState
    );
  }, [artifact, form, open, syncAudioState, syncImageState]);

  const modalTitle = mode === 'create' ? 'Add Artifact' : 'Edit Artifact';
  const isSaving = isCreating || isUpdating || isPreparingSave;
  const effectiveTranscript = currentTranscript || artifact?.transcript || null;
  const isAnyUploadInFlight =
    imageState.phase === 'uploading' ||
    imageState.phase === 'processing' ||
    audioState.phase === 'uploading' ||
    audioState.phase === 'processing';

  const canSave = useMemo(() => !isAnyUploadInFlight && !isSaving, [isAnyUploadInFlight, isSaving]);
  const titleLength = Form.useWatch('title', form)?.length || 0;
  const descriptionLength = Form.useWatch('description', form)?.length || 0;

  const validateRequiredFields = async () => {
    const values = form.getFieldsValue();
    const title = (values.title || '').trim();
    const currentImageMedia = imageStateRef.current.media;

    if (!title) {
      throw new Error('A title is required.');
    }

    if (!currentImageMedia?.id) {
      throw new Error('A primary image is required before saving.');
    }
  };

  const handleRetryTranscription = async () => {
    if (!artifact) {
      return;
    }

    try {
      const updatedArtifact = await retryTranscription({
        artifactId: artifact.id,
        expectedRevision: currentRevision
      }).unwrap();
      setCurrentRevision(updatedArtifact.revision);
      setCurrentTranscript(updatedArtifact.transcript);
      setTranscriptDraft(updatedArtifact.transcript.text || '');
      onSaved?.(updatedArtifact);
    } catch (error: any) {
      setSaveErrorMessage(getApiErrorMessage(error));
    }
  };

  const handleSave = async () => {
    setSaveErrorMessage('');

    if (mode === 'edit' && !artifact) {
      setSaveErrorMessage('The artifact to edit is missing.');
      return;
    }

    try {
      setIsPreparingSave(true);
      const finalDescription = await descriptionRef.current?.flush();
      const currentDescription = form.getFieldValue('description');

      if (typeof finalDescription === 'string' && finalDescription !== currentDescription) {
        form.setFieldValue('description', finalDescription);
      }

      await form.validateFields();
      await validateRequiredFields();

      const finalizedAudioMedia = await audioFieldRef.current?.finalizeRecordingForSave();
      const values = form.getFieldsValue();
      const currentImageMedia = imageStateRef.current.media;
      const currentAudioMedia = finalizedAudioMedia === undefined ? audioStateRef.current.media : finalizedAudioMedia;

      if (mode === 'create') {
        const createdArtifact = await createArtifact({
          title: values.title.trim(),
          description: values.description?.trim() || null,
          memoryDate: values.memoryDate || null,
          memoryDateLabel: values.memoryDateLabel?.trim() || null,
          imageMediaId: currentImageMedia!.id,
          audioMediaId: currentAudioMedia?.id || null
        }).unwrap();

        onSaved?.(createdArtifact);
        onClose();
        return;
      }

      const artifactToEdit = artifact!;
      const patch: UpdateMyArtifactPayload['patch'] = {
        title: values.title.trim(),
        description: values.description?.trim() || null,
        memoryDate: values.memoryDate || null,
        memoryDateLabel: values.memoryDateLabel?.trim() || null,
        transcriptText: effectiveTranscript ? transcriptDraft.trim() || null : undefined
      };

      if ((currentImageMedia?.id || null) !== (artifactToEdit.image?.id || null)) {
        patch.imageMediaId = currentImageMedia?.id || null;
      }

      if ((currentAudioMedia?.id || null) !== (artifactToEdit.audio?.id || null)) {
        patch.audioMediaId = currentAudioMedia?.id || null;
      }

      const updatedArtifact = await updateArtifact({
        artifactId: artifactToEdit.id,
        expectedRevision: currentRevision,
        patch
      }).unwrap();

      onSaved?.(updatedArtifact);
      onClose();
    } catch (error: any) {
      setSaveErrorMessage(error?.message || getApiErrorMessage(error));
    } finally {
      setIsPreparingSave(false);
    }
  };

  return (
    <Modal
      open={open}
      destroyOnHidden
      width={860}
      title={<div style={{ color: '#1f1b37', fontSize: 24, fontWeight: 700 }}>{modalTitle}</div>}
      onCancel={onClose}
      styles={{ body: { background: '#fcfcff', padding: '20px 24px 24px' } }}
      footer={[
        <Button
          key="cancel"
          size="large"
          onClick={onClose}
          style={{ borderRadius: 12, fontWeight: 600, minWidth: 108 }}
        >
          Cancel
        </Button>,
        <Button
          key="save"
          type="primary"
          size="large"
          loading={isSaving}
          disabled={!canSave}
          onClick={handleSave}
          style={{ borderRadius: 12, fontWeight: 700, minWidth: 132 }}
        >
          {mode === 'create' ? 'Save Artifact' : 'Save Changes'}
        </Button>
      ]}
    >
      <Space direction="vertical" size={16} style={{ width: '100%' }}>
        {saveErrorMessage && <Alert type="error" showIcon message={saveErrorMessage} />}

        {isAnyUploadInFlight && (
          <Alert
            type="info"
            showIcon
            message="Media is still uploading or processing. Saving will be enabled when it is ready."
          />
        )}

        <ArtifactImageField initialMedia={artifact?.image || null} onChange={syncImageState} />

        <div style={sectionContainerStyle}>
          <Form form={form} layout="vertical" requiredMark={false}>
            <Form.Item
              label={renderSectionTitle(2, 'Title', undefined, true)}
              name="title"
              rules={[{ required: true, message: 'Title is required.' }]}
              extra={
                <div style={fieldCounterWrapStyle}>
                  <Text style={fieldCounterTextStyle}>{titleLength}/100</Text>
                </div>
              }
              style={{ marginBottom: 16 }}
            >
              <Input
                maxLength={100}
                showCount={false}
                placeholder="Enter a title for this artifact"
                style={inputStyle}
              />
            </Form.Item>

            <Form.Item
              label={renderSectionTitle(3, 'Story Description')}
              name="description"
              extra={
                <div style={fieldCounterWrapStyle}>
                  <Text style={fieldCounterTextStyle}>{descriptionLength}/1000</Text>
                </div>
              }
              style={{ marginBottom: 16 }}
            >
              <SpeechAntdTextArea
                ref={descriptionRef}
                rows={4}
                maxLength={1000}
                showCount={false}
                placeholder="Share the story behind this artifact..."
              />
            </Form.Item>

            <div className="memory-date-row">
              <Form.Item
                className="memory-date-field"
                label={<Text style={{ color: '#4b536a', fontWeight: 600 }}>Memory Date</Text>}
                name="memoryDate"
                style={{ marginBottom: 0 }}
              >
                <ArtifactMemoryDatePicker />
              </Form.Item>

              <Form.Item
                className="memory-date-field"
                label={<Text style={{ color: '#4b536a', fontWeight: 600 }}>Date Label</Text>}
                name="memoryDateLabel"
                style={{ marginBottom: 0 }}
              >
                <Input placeholder="Optional label such as Summer 1984" style={inputStyle} />
              </Form.Item>
            </div>

          </Form>
        </div>

        <ArtifactAudioField ref={audioFieldRef} initialMedia={artifact?.audio || null} onChange={syncAudioState} />

        {effectiveTranscript && (
          <ArtifactTranscriptPanel
            mode={mode}
            transcript={effectiveTranscript}
            value={transcriptDraft}
            onChange={setTranscriptDraft}
            onRetry={handleRetryTranscription}
            isRetrying={isRetryingTranscription}
          />
        )}
      </Space>
    </Modal>
  );
}

export default ArtifactEditorModal;
